import { ReactNode } from 'react';

import { AppIcon, AppIcons, DonutChart, PriorityPlaylist } from '@backrs_ui/common-ui';

import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';

import { Box, Theme, SxProps, Typography, Chip, Button } from '@mui/material';

const PLAYLIST_CARD_STRINGS = appStrings.modules.priorities.playlistCard;

export type PlaylistCardProps = Readonly<{
  playlist: PriorityPlaylist;
  onClick: () => void;
  playlistCardWidth?: number;
  showDateOrDescription?: 'DATE' | 'DESCRIPTION';
  opacity?: number;
  topLeftElement?:
    | {
        type: 'DONUT';
      }
    | { type: 'BUTTON_WITH_ICON'; iconName: AppIcons }
    | { type: 'IMAGE'; imageUrl: string };

  topRightElement?: { type: 'TASK_COUNT' } | { type: 'ELEMENT'; element: ReactNode };
  bottomElement?:
    | { type: 'BUTTON_WITH_ICON'; iconName: AppIcons; label: string }
    | { type: 'ELEMENT'; element: ReactNode };
}>;

export const PlaylistCard = ({
  playlist,
  onClick,
  playlistCardWidth = 286,
  showDateOrDescription = 'DATE',
  opacity = 1,
  topLeftElement = { type: 'DONUT' },
  topRightElement = { type: 'TASK_COUNT' },
  bottomElement,
}: PlaylistCardProps) => {
  const percentageFilled =
    !!playlist.completed_tasks && !!playlist.total_tasks
      ? (playlist.completed_tasks / playlist.total_tasks) * 100
      : 0;

  const isThereABottomButton = !!bottomElement && bottomElement.type === 'BUTTON_WITH_ICON';

  return (
    <Box sx={containerSx(playlistCardWidth)} onClick={onClick}>
      <Box sx={taskHeaderContainerSx}>
        <Box sx={donutTitleChartContainerSx(opacity)}>
          {/* DONUT CHART */}
          {topLeftElement.type === 'DONUT' && (
            <DonutChart
              variant={{
                type: 'solo',
                percentageFilled,
              }}
              size={52}
              isTooltipEnabled={false}
            />
          )}

          {/* BUTTON WITH ICON */}
          {topLeftElement.type === 'BUTTON_WITH_ICON' && (
            <Button
              id="playlist-card-icon-button"
              icon="Only"
              size="sm"
              hierarchy="Secondary gray"
              sx={featuredIconButtonSx}
            >
              <AppIcon
                iconName={topLeftElement.iconName}
                diameter="24px"
                color="Colors/Base/Black"
              />
            </Button>
          )}

          {/* IMAGE */}
          {topLeftElement.type === 'IMAGE' && <Box component="img" src={topLeftElement.imageUrl} />}

          {/* TITLE AND DATE */}
          <Box sx={taskHeaderTextContainerSx}>
            <Typography variant="text-lg-semibold" sx={titleSx}>
              {playlist.title}
            </Typography>
            <Typography variant="text-xs-regular">
              {showDateOrDescription === 'DATE' &&
                new Date(playlist.created_at).toLocaleDateString('en-US', {
                  month: 'long',
                  day: 'numeric',
                  year: 'numeric',
                })}

              {showDateOrDescription === 'DESCRIPTION' && playlist.description}
            </Typography>
          </Box>
        </Box>

        {/* TASKS COUNT */}
        {topRightElement?.type === 'TASK_COUNT' && (
          <Chip
            variant="pill"
            size="sm"
            label={PLAYLIST_CARD_STRINGS.tasksCount(playlist.total_tasks)}
            sx={chipSx}
          />
        )}

        {topRightElement?.type === 'ELEMENT' && topRightElement.element}
      </Box>

      {/* DESCRIPTION - SHOWN IF DESCRIPTION IS NOT DISPLAYED IN TITLE SECTION */}
      {showDateOrDescription !== 'DESCRIPTION' && (
        <Typography variant="text-sm-regular" sx={descriptionSx(opacity)}>
          {playlist.description}
        </Typography>
      )}

      {/* BOTTOM ELEMENT */}
      {isThereABottomButton && (
        <Button
          id="playlist-card-action-button"
          icon="Default"
          size="md"
          hierarchy="Secondary gray"
          sx={bottomElementButtonSx(opacity)}
        >
          <AppIcon iconName={bottomElement.iconName} diameter="20px" color="currentColor" />

          <Typography variant="text-sm-semibold">{bottomElement.label}</Typography>
        </Button>
      )}

      {bottomElement?.type === 'ELEMENT' && <Box sx={{ opacity }}>{bottomElement.element}</Box>}
    </Box>
  );
};

const containerSx =
  (playlistCardWidth: number): SxProps<Theme> =>
  ({ backrs }) => ({
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    width: '100%',
    maxWidth: { sm: `${playlistCardWidth}px` },
    minWidth: { sm: '100%' },
    border: `1px solid ${backrs.colors['Colors/Foreground/fg-primary']}`,
    backgroundColor: backrs.colors['Colors/Background/bg-primary'],
    padding: backrs.spacing['spacing-2xl'],
    borderRadius: backrs.radius['radius-lg'],

    gap: backrs.spacing['spacing-3xl'],
    cursor: 'pointer',
    userSelect: 'none',

    transition: 'all 0.3s ease-in-out',

    '&:hover': {
      borderColor: backrs.colors['Colors/Foreground/fg-primary'],
      transform: 'translate(-2px, -2px)',
      boxShadow: backrs.shadows['shadow-lg'],
    },

    '&:active': {
      transform: 'none !important',
      boxShadow: 'none !important',
      transition: 'all 0s !important',
    },
  });

const taskHeaderContainerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'space-between',
  gap: backrs.spacing['spacing-lg'],
});

const chipSx: SxProps<Theme> = ({ backrs }) => ({
  backgroundColor: backrs.colors['Colors/Background/bg-active'],
  color: backrs.colors['Colors/Text/text-secondary'],
  mt: backrs.spacing['spacing-xs'],
});

const taskHeaderTextContainerSx: SxProps<Theme> = ({ backrs }) => ({
  mt: backrs.spacing['spacing-xs'],
  alignSelf: 'center',
});

const donutTitleChartContainerSx =
  (opacity: number): SxProps<Theme> =>
  ({ backrs }) => ({
    display: 'flex',
    gap: backrs.spacing['spacing-lg'],
    opacity,
  });

const titleSx: SxProps<Theme> = {
  lineHeight: '1.3',
  display: '-webkit-box',
  WebkitLineClamp: 3,
  WebkitBoxOrient: 'vertical',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  wordBreak: 'break-word',
};

const descriptionSx =
  (opacity: number): SxProps<Theme> =>
  () => ({
    display: '-webkit-box',
    WebkitLineClamp: 5,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    opacity,
  });

const featuredIconButtonSx: SxProps<Theme> = ({ backrs }) => ({
  padding: backrs.spacing['spacing-lg'],
  borderRadius: backrs.radius['radius-lg'],
  borderColor: backrs.colors['Gray/300'],
});

const bottomElementButtonSx =
  (opacity: number): SxProps<Theme> =>
  ({ backrs }) => ({
    padding: backrs.spacing['spacing-lg'],
    borderRadius: backrs.radius['radius-lg'],
    borderWidth: '1px',
    borderColor: backrs.colors['Brand/300'],
    color: backrs.colors['Brand/700'],
    height: '40px',

    opacity,

    '&:hover': {
      color: backrs.colors['Brand/700'],
      borderColor: backrs.colors['Brand/700'],
    },

    '&:active': {
      color: backrs.colors['Brand/700'],
      borderColor: backrs.colors['Brand/700'],
    },
  });
